import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Loader2, ChevronDown, ChevronRight, Pause, Play } from "lucide-react";
import type {
  ScoreApiResponse,
  StaffRole,
  UserProfile,
  ConversationEntry,
} from "../types";
import ErrorModal from "../components/error-modal";
import { HighlightedConversation } from "../components/HighlightedConversation";
import { DEMO_TEACHER_BASE, demoEvaluationHref } from '../lib/demo-teacher';
import ConversationLog from "../components/conversation-log";
import {
  AvatarFaceIcon,
  LearnerIcon,
  dentistStillImageUrl,
  patientStillImageUrl,
} from "../components/ConversationLogAvatar";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { format } from "date-fns";
import { getScores, type ScoreRow, type ScoresResponse } from "../lib/scores-api";
import { getPublicStudentScoreSample } from "../lib/admin-api";
import { QuestionnaireDocument, type QuestionnaireJsonData } from "../components/QuestionnaireDocument";
import { InstructionPreviewBody } from "../components/InstructionPreviewBody";
import { ExperimentPostSurveyForm } from "../components/ExperimentPostSurveyForm";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useIsMobile } from "../hooks/use-mobile";
import { EvaluationTaskReview } from "../components/EvaluationTaskReview";
import { buildPatientReview, buildReportReview } from "../lib/evaluation-review";
import type { ProgressSourceTask } from "../lib/interview-task-progress";
import type { InstructionItemExamples } from "../types";
import { AuthService } from "../lib/auth";
import {
  isExperimentDirectEntryUser,
  rememberFinishedInterviewId,
  startFreshExperimentDirectInterview,
} from "../lib/experiment-direct-entry";
import { EXPERIMENT_PRIMARY_ACTION_CLASS } from "../lib/experiment-ui";
import { hasReportScore, toLearningScore } from '../lib/learning-score';
import { naCategoryNamesFor } from '../lib/patient-score-categories';
import { isInterviewFinishedHistoryState } from '../lib/browserBackGuard';
import { useBrowserBackGuard } from '../hooks/useBrowserBackGuard';
import { STUDENT_HEADER_HEIGHT } from "../components/student/StudentHeader";

// ===== Design tokens (select-filter / scenario-preview と共通) =====
const COLORS = {
  bg: "#FAFAF7",
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  line: "#E4E4E7",
  paper: "#FDFCF7",
  paperLine: "#ECE5D4",
  strip: "#FAF7EF",
  stripDot: "#E8DFC4",
};

interface EvaluationConversationApiEntry {
  role: string;
  content: string;
  timestamp: number;
  speakerType?: string;
  roundId?: number;
  hasUserAudio?: boolean;
}

function StaffRecordingPlaybackButton({ roundId }: { roundId: number }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);

  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      audio.src = "";
      audioRef.current = null;
    };
  }, [roundId]);

  const togglePlayback = async () => {
    setPlaybackError(false);
    let audio = audioRef.current;
    if (!audio) {
      audio = new Audio(`/api/admin/recordings/rounds/${roundId}/user-audio`);
      audio.preload = "metadata";
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => {
        setIsPlaying(false);
        setPlaybackError(true);
      };
      audioRef.current = audio;
    }

    if (!audio.paused) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    try {
      await audio.play();
      setIsPlaying(true);
    } catch {
      setIsPlaying(false);
      setPlaybackError(true);
    }
  };

  return (
    <div className="mt-2 border-t border-orange-200 pt-2">
      <button
        type="button"
        onClick={togglePlayback}
        title={isPlaying ? "録音を一時停止" : "この発話の録音を再生"}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-orange-300 bg-white px-2.5 text-xs font-bold text-orange-800 transition hover:border-orange-400 hover:bg-orange-50"
      >
        {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        {isPlaying ? "一時停止" : "録音を再生"}
      </button>
      {playbackError && (
        <p className="mt-1 text-xs font-bold text-red-700">録音を再生できませんでした。</p>
      )}
    </div>
  );
}

// ===== Rank chip (難易度チップの同じ形を踏襲) =====
type Rank = "A" | "B" | "C" | "D";
const RANK_META: Record<Rank, { label: string; sub: string; color: string; soft: string; dots: string }> = {
  A: { label: "A", sub: "優秀",   color: "#15803D", soft: "#DCFCE7", dots: "●●●" },
  B: { label: "B", sub: "良好",   color: "#0E7490", soft: "#ECFEFF", dots: "●●○" },
  C: { label: "C", sub: "あと一歩", color: "#B45309", soft: "#FEF3C7", dots: "●○○" },
  D: { label: "D", sub: "要再挑戦", color: "#BE123C", soft: "#FFF1F2", dots: "○○○" },
};

/** 総評を「良かった点」「改善できる点」にパース */
function parseSummarySections(text: string): { goodPoints: string; improvePoints: string } | null {
  const goodMatch = text.match(/【良かった点】\s*([\s\S]*?)(?=【改善できる点】|$)/);
  const improveMatch = text.match(/【改善できる点】\s*([\s\S]*)$/);
  if (goodMatch || improveMatch) {
    return {
      goodPoints: goodMatch ? goodMatch[1].trim() : "",
      improvePoints: improveMatch ? improveMatch[1].trim() : "",
    };
  }
  return null;
}

function getRank(pct: number): Rank {
  if (pct >= 80) return "A";
  if (pct >= 60) return "B";
  if (pct >= 40) return "C";
  return "D";
}

type ScoreHistoryPoint = {
  interviewId: number;
  date: string;
  dateLabel: string;
  総合: number;
  患者面接: number | null;
  歯科医報告: number | null;
};

const FONT_FAMILY = '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif';

export default function Evaluation() {
  const [errorMessage] = useState("");
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [location, setLocation] = useLocation();
  const [scoreAnimation, setScoreAnimation] = useState(0);
  const [docTab, setDocTab] = useState<"questionnaire" | "instruction">("instruction");
  const [isSurveyCompletionOpen, setIsSurveyCompletionOpen] = useState(false);
  const isMobile = useIsMobile();
  const authService = AuthService.getInstance();
  // 面接を終えた直後の評価では、ブラウザの戻る操作で医師の説明画面へ戻らず、この画面に留まる。
  const [cameFromFinishedInterview] = useState(
    () => typeof window !== "undefined" && isInterviewFinishedHistoryState(window.history.state),
  );
  useBrowserBackGuard(cameFromFinishedInterview, () => {});

  const searchString = (typeof location === "string" && location.includes("?"))
    ? "?" + location.split("?")[1]
    : typeof window !== "undefined"
    ? window.location.search
    : "";
  const params = new URLSearchParams(searchString);
  const token = params.get("token");
  const interviewId = params.get("interview_id");
  const publicParticipantCode = params.get("demo_participant");
  const isPublicSample =
    publicParticipantCode != null && /^\d{3}$/.test(publicParticipantCode);
  // 教員画面の公開デモ（/demo/teacher）から開いたときは、戻り先もデモの中にする
  const requestedDemoReturn = params.get("demo_return");
  const teacherDemoReturn =
    isPublicSample && requestedDemoReturn?.startsWith(DEMO_TEACHER_BASE) ? requestedDemoReturn : null;
  useEffect(() => {
    if (!interviewId) return;
    const parsedInterviewId = Number.parseInt(interviewId, 10);
    if (Number.isInteger(parsedInterviewId) && parsedInterviewId > 0) {
      rememberFinishedInterviewId(parsedInterviewId);
    }
  }, [interviewId]);
  const scoreUrl = interviewId && isPublicSample
    ? `/api/public/ranking-sample/students/${encodeURIComponent(publicParticipantCode)}/interviews/${interviewId}`
    : interviewId
    ? `/api/score/by-interview/${interviewId}`
    : token
    ? `/api/score?token=${token}`
    : "/api/score";
  const conversationHistoryUrl = !interviewId && token ? `/api/conversation-history?token=${token}` : !interviewId ? "/api/conversation-history" : null;
  const queryClient = useQueryClient();

  const { data: currentUser } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => authService.getCurrentUser(),
    staleTime: 5 * 60 * 1000,
    enabled: !isPublicSample,
  });
  const currentRole = currentUser?.role as StaffRole | undefined;
  const scoreListTarget = teacherDemoReturn
    ? teacherDemoReturn
    : isPublicSample
    ? `/demo/rankings/${encodeURIComponent(publicParticipantCode)}`
    : currentRole === "student"
      ? "/mypage#interview-results"
      : currentRole === "educator"
      ? "/teacher/scores"
      : currentRole === "admin"
      ? "/admin/scores"
      : "/scores";
  const scoreListLabel = teacherDemoReturn
    ? (teacherDemoReturn === `${DEMO_TEACHER_BASE}/scores` ? "面接評価一覧へ" : "学生のスコアへ")
    : isPublicSample
    ? "参加者のスコアへ"
    : currentRole === "student"
      ? "面接結果一覧へ"
      : currentRole === "educator"
      ? "担当学生スコアへ"
      : "スコア一覧へ";
  const handleBackToScores = () => setLocation(scoreListTarget);

  const { data: apiResponse, isLoading, error } = useQuery<ScoreApiResponse & { conversations?: { conversations: EvaluationConversationApiEntry[] } }>({
    queryKey: [scoreUrl],
    staleTime: 0,
    enabled: !!scoreUrl,
  });

  const { data: conversationDataFromApi } = useQuery<{ conversations: EvaluationConversationApiEntry[]; categoryTableText?: string; visitHistory?: string }>({
    queryKey: [conversationHistoryUrl],
    staleTime: 0,
    enabled: !!conversationHistoryUrl && !!(apiResponse?.data || apiResponse?.dentistData),
  });
  const conversationData = interviewId && (apiResponse as any)?.conversations
    ? (apiResponse as any).conversations
    : conversationDataFromApi;

  const documentsUrl = isPublicSample
    ? null
    : token
    ? null
    : interviewId
    ? `/api/current-interview/documents?interview_id=${interviewId}`
    : "/api/current-interview/documents";
  const { data: fetchedDocumentsData } = useQuery<{
    questionnaire: string;
    questionnaireJson?: QuestionnaireJsonData | null;
    visitHistory?: string | null;
    instruction: {
      title?: string;
      summary?: string;
      context?: Record<string, unknown>;
      target_categories?: string[];
      target_category_labels?: Record<string, string>;
      task_descriptions?: Array<{ title: string; description: string }>;
      tasks?: Array<{ no: number; title: string; details?: string[] } & ProgressSourceTask>;
      /** 課題文の元データ。正解例（item_examples）を読む */
      raw?: { item_examples?: InstructionItemExamples } | null;
    } | null;
    patient?: { name: string | null; age: number | null; gender: string | null; birthDate?: string | null };
  }>({
    queryKey: [documentsUrl],
    staleTime: 0,
    enabled: !!documentsUrl && !!(apiResponse?.data || apiResponse?.dentistData),
  });
  const documentsData = isPublicSample
    ? (apiResponse as any)?.documents
    : fetchedDocumentsData;

  const { data: scoreHistoryData, isError: scoreHistoryError } = useQuery<ScoresResponse>({
    queryKey: ["score-history", isPublicSample ? publicParticipantCode : "authenticated"],
    queryFn: async () => {
      if (!isPublicSample || !publicParticipantCode) {
        return getScores({ limit: 30 });
      }
      const publicScores = await getPublicStudentScoreSample(publicParticipantCode);
      return {
        scores: publicScores.scores.slice(0, 30).map((score) => ({
          interview_id: score.interviewId,
          user_id: Number(publicParticipantCode),
          patient_score_value: score.patientScore ?? undefined,
          dentist_score_value: score.dentistScore ?? undefined,
          overall_score_value: score.overallScore,
          created: score.created,
        })),
        statistics: {
          avg: publicScores.summary.averageScore,
          min: 0,
          max: 100,
        },
        pagination: {
          page: 1,
          limit: 30,
          total: publicScores.summary.evaluatedCount,
        },
      };
    },
    staleTime: 60_000,
    retry: false,
    enabled: !!(apiResponse?.data || apiResponse?.dentistData),
  });

  const scoringResult = apiResponse?.data;
  const dentistResult = apiResponse?.dentistData;
  const scorePending = apiResponse?.status === "pending" || apiResponse?.status === "running";
  const onePointLesson = apiResponse?.onePointLesson;
  const visibleScore = apiResponse?.visibleScore;
  const patientNaCategories = new Set(apiResponse?.patientNaCategories ?? []);
  const isDirectEntryUser = !isPublicSample && isExperimentDirectEntryUser(currentUser);
  const parsedInterviewId = interviewId ? Number.parseInt(interviewId, 10) : NaN;
  const showExperimentSurvey =
    isDirectEntryUser &&
    currentRole === "student" &&
    Number.isInteger(parsedInterviewId) &&
    parsedInterviewId > 0;
  const renderStaffRecordingAction = (entry: ConversationEntry) => {
    if (
      (currentRole !== "admin" && currentRole !== "educator") ||
      entry.type !== "question" ||
      !entry.hasUserAudio ||
      !entry.roundId
    ) {
      return null;
    }
    return <StaffRecordingPlaybackButton roundId={entry.roundId} />;
  };
  const usesDentistReportScore = hasReportScore(visibleScore, dentistResult);

  const naCategoryNames = naCategoryNamesFor(patientNaCategories, scoringResult?.itemScores);

  const filteredCategoryScores = scoringResult?.categoryScores
    ? Object.fromEntries(
        Object.entries(scoringResult.categoryScores).filter(([name]) => !naCategoryNames.has(name))
      )
    : {};
  const filteredTotal = Object.values(filteredCategoryScores).reduce(
    (s, d) => s + (typeof d?.score === "number" ? d.score : 0),
    0
  );
  const filteredMax = Object.values(filteredCategoryScores).reduce(
    (s, d) => s + (typeof d?.maxScore === "number" ? d.maxScore : 0),
    0
  );
  const fallbackScorePercentage = filteredMax > 0 ? Math.round((filteredTotal / filteredMax) * 100) : 0;
  const scorePercentage = visibleScore?.patient?.value ?? fallbackScorePercentage;

  const dentistNumerator = usesDentistReportScore && dentistResult
    ? ((dentistResult as any).finalScore ?? (dentistResult as any).totalScore ?? 0)
    : 0;
  const dentistDenominator = usesDentistReportScore && dentistResult ? ((dentistResult as any).maxScore ?? 0) : 0;
  const fallbackDentistScorePercentage = dentistDenominator > 0
    ? Math.round((dentistNumerator / dentistDenominator) * 100)
    : 0;
  const dentistScorePercentage = visibleScore?.dentist?.value ?? fallbackDentistScorePercentage;

  // ふりかえり（#14）: 課題文のタスクごとに、患者面接と報告をタブで切り替えて見せる
  const reviewTasks = documentsData?.instruction?.tasks as ProgressSourceTask[] | undefined;
  const reviewExamples = (documentsData?.instruction?.raw as { item_examples?: InstructionItemExamples } | null | undefined)
    ?.item_examples;
  const patientTaskReview = useMemo(
    () => buildPatientReview(scoringResult, reviewTasks, reviewExamples),
    [scoringResult, reviewTasks, reviewExamples],
  );
  const reportTaskReview = useMemo(
    () => (usesDentistReportScore ? buildReportReview(dentistResult, scoringResult, reviewTasks, reviewExamples) : null),
    [dentistResult, reviewExamples, reviewTasks, scoringResult, usesDentistReportScore],
  );

  // 総合スコア = (患者面接 + 歯科医報告) の分子合計 / 分母合計
  const combinedNumerator = filteredTotal + dentistNumerator;
  const combinedDenominator = filteredMax + dentistDenominator;
  const fallbackTotalPercentage = combinedDenominator > 0
    ? Math.round((combinedNumerator / combinedDenominator) * 100)
    : 0;
  const totalPercentage = visibleScore?.overall?.value ?? fallbackTotalPercentage;
  const { score: learningScore, max: learningScoreMax } = toLearningScore(totalPercentage, usesDentistReportScore);
  const totalRank = getRank(totalPercentage);
  const rankMeta = RANK_META[totalRank];

  useEffect(() => {
    if (learningScore <= 0) {
      setScoreAnimation(0);
      return;
    }
    let raf = 0;
    const duration = 1400;
    const easeOutExpo = (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
    const startTs = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTs) / duration);
      setScoreAnimation(learningScore * easeOutExpo(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [learningScore]);

  const directEntryMutation = useMutation({
    mutationFn: async () => {
      if (!currentUser?.experimentEntryTarget) {
        throw new Error("実証実験の直接面接情報が見つかりません");
      }
      return startFreshExperimentDirectInterview(currentUser.experimentEntryTarget);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
      setLocation("/interview");
    },
  });

  const handleBackToSelect = () => {
    if (isPublicSample) {
      setLocation(scoreListTarget);
      return;
    }
    if (isDirectEntryUser) {
      directEntryMutation.mutate();
      return;
    }
    setLocation("/select-filter");
  };
  const handleReturnToEvaluation = () => {
    setIsSurveyCompletionOpen(false);
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>("[data-evaluation-scroll-container]")?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  };
  const handleStartInterviewAfterSurvey = () => {
    setIsSurveyCompletionOpen(false);
    handleBackToSelect();
  };
  const backToSelectLabel = isDirectEntryUser
    ? "面接を始める"
    : "新しいシナリオを選ぶ →";

  const retryWithInstructionMutation = useMutation({
    mutationFn: async (params: { instructionId: number; avatarId?: number }) => {
      const body: Record<string, unknown> = { instructionId: params.instructionId, restart: true };
      if (params.avatarId) body.avatarId = params.avatarId;
      const response = await fetch("/api/start-interview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
      setLocation("/interview");
    },
  });

  const interviewMeta = (apiResponse as any)?.interviewMeta as {
    mode?: string; conditionInstructionId?: number; gender?: string; ageRange?: string; avatarId?: number; avatarName?: string;
    dentistGender?: string | null;
  } | null;
  // 会話ログの発言者アイコン。学習者のプロフィール画像は本人が見ているときだけ出す。
  const viewerIsLearner = (apiResponse as any)?.viewerIsLearner === true;
  const patientLogIcon = (
    <AvatarFaceIcon
      src={patientStillImageUrl(interviewMeta?.gender, interviewMeta?.ageRange)}
      label={interviewMeta?.avatarName || "患者"}
    />
  );
  const dentistLogIcon = <AvatarFaceIcon src={dentistStillImageUrl(interviewMeta?.dentistGender)} label="歯科医" />;
  const learnerLogIcon = <LearnerIcon showProfileImage={viewerIsLearner} />;
  const retryButtonLabel = "もう一度面接する";

  useEffect(() => {
    if (error) {
      const msg = (error as Error)?.message ?? "";
      const status = (error as any)?.status ?? (error as any)?.response?.status ?? (msg.startsWith("404") ? 404 : null);
      if (status === 404) setLocation(scoreListTarget);
    }
  }, [error, setLocation, scoreListTarget]);

  useEffect(() => {
    if (!scoreUrl || !scorePending) return;
    const timer = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: [scoreUrl] });
    }, 2500);
    return () => window.clearInterval(timer);
  }, [queryClient, scorePending, scoreUrl]);

  const evidenceStrings = [
    ...(scoringResult ? (scoringResult.itemScores ?? []).filter((it: { score?: number; evidence?: string }) => (it.score ?? 0) > 0 && it.evidence?.trim()).map((it: { evidence?: string }) => it.evidence!.trim()) : []),
    ...(dentistResult ? (dentistResult.itemScores ?? []).filter((it: { isReported?: boolean; evidence?: string }) => it.isReported && it.evidence?.trim()).map((it: { evidence?: string }) => it.evidence!.trim()) : []),
  ];

  const patientSections = scoringResult?.summaryComment ? parseSummarySections(scoringResult.summaryComment) : null;
  const dentistSections = dentistResult?.summaryComment ? parseSummarySections(dentistResult.summaryComment) : null;
  const patientSummaryPending = scoringResult?.summaryStatus === "pending";
  const dentistSummaryPending = dentistResult?.summaryStatus === "pending";
  const patientSummaryPendingText = patientSummaryPending
    ? "患者面接の総評を生成中です。点数とふりかえりは先に確認できます。"
    : undefined;
  const dentistSummaryPendingText = dentistSummaryPending
    ? "歯科医報告の総評を生成中です。点数とふりかえりは先に確認できます。"
    : undefined;

  useEffect(() => {
    if (!scoreUrl || (!patientSummaryPending && !dentistSummaryPending)) return;
    const timer = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: [scoreUrl] });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [dentistSummaryPending, patientSummaryPending, queryClient, scoreUrl]);

  useEffect(() => {
    if (!isMobile) return;

    window.scrollTo({ top: 0, left: 0 });

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyOverscroll = document.body.style.overscrollBehaviorY;
    const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorY;

    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehaviorY = "none";
    document.documentElement.style.overscrollBehaviorY = "none";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.overscrollBehaviorY = previousBodyOverscroll;
      document.documentElement.style.overscrollBehaviorY = previousHtmlOverscroll;
    };
  }, [isMobile]);

  const hasEvaluationDocuments =
    Boolean(documentsData?.questionnaireJson) ||
    Boolean(documentsData?.questionnaire) ||
    Boolean(conversationData?.categoryTableText) ||
    Boolean(documentsData?.instruction);
  const showEvaluationDocuments = hasEvaluationDocuments;
  const scoreHistoryPoints: ScoreHistoryPoint[] = (scoreHistoryData?.scores ?? [])
    .map((row: ScoreRow) => {
      const p = row.patient_score_value ?? null;
      const d = usesDentistReportScore ? row.dentist_score_value ?? null : null;
      const total = row.overall_score_value
        ?? (usesDentistReportScore && p != null && d != null ? (p + d) / 2 : p ?? d ?? 0);
      return {
        interviewId: row.interview_id,
        date: row.created,
        dateLabel: format(new Date(row.created), "M/d"),
        総合: Math.round(total),
        患者面接: p != null ? Math.round(p) : null,
        歯科医報告: d != null ? Math.round(d) : null,
      };
    })
    .reverse();

  const scoreHistoryTarget = (payload?: { interviewId?: number }) => {
    if (!payload?.interviewId) return;
    if (teacherDemoReturn && publicParticipantCode) {
      return demoEvaluationHref(payload.interviewId, publicParticipantCode, teacherDemoReturn);
    }
    return isPublicSample && publicParticipantCode
      ? `/evaluation?interview_id=${payload.interviewId}&demo_participant=${encodeURIComponent(publicParticipantCode)}`
      : `/evaluation?interview_id=${payload.interviewId}`;
  };

  const navigateToScoreHistoryPoint = (payload?: { interviewId?: number }) => {
    const target = scoreHistoryTarget(payload);
    if (!target) return;
    if (typeof window !== "undefined") {
      window.location.href = target;
      return;
    }
    if (location !== target) setLocation(target);
  };

  const handleScoreHistoryChartClick = (state: any) => {
    const payload = state?.activePayload?.find((entry: { payload?: ScoreHistoryPoint }) => entry?.payload?.interviewId)?.payload;
    navigateToScoreHistoryPoint(payload);
  };

  const renderScoreHistoryDot = (
    props: any,
    color: string,
    radius: number,
  ) => {
    const { cx, cy, payload } = props;
    if (typeof cx !== "number" || typeof cy !== "number" || !payload?.interviewId) return <g />;
    const href = scoreHistoryTarget(payload);
    return (
      <a
        href={href}
        style={{ cursor: "pointer", pointerEvents: "all" }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onTouchStart={(event) => {
          event.stopPropagation();
        }}
      >
        <title>{`${payload.dateLabel} の評価結果を開く`}</title>
        <circle cx={cx} cy={cy} r={radius + 10} fill="transparent" pointerEvents="all" />
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill={color}
          stroke={COLORS.surface}
          strokeWidth={1.5}
          pointerEvents="all"
        />
      </a>
    );
  };

  const renderEvaluationDocumentsCard = (compact = false) => {
    if (!showEvaluationDocuments) return null;
    return (
      <CollapsibleCard label="課題文・問診票">
        <div style={{ paddingTop: 12 }}>
          <div
            style={{
              display: compact ? "grid" : "flex",
              gridTemplateColumns: compact ? "1fr 1fr" : undefined,
              border: compact ? `1px solid ${COLORS.line}` : undefined,
              borderBottom: compact ? undefined : `1px solid ${COLORS.line}`,
              borderRadius: compact ? 10 : undefined,
              overflow: compact ? "hidden" : undefined,
              gap: 0,
              marginBottom: 16,
            }}
          >
            <DocTabButton active={docTab === "instruction"} onClick={() => setDocTab("instruction")}>
              課題文
            </DocTabButton>
            <DocTabButton active={docTab === "questionnaire"} onClick={() => setDocTab("questionnaire")}>
              問診票
            </DocTabButton>
          </div>
          {docTab === "instruction" ? (
            documentsData?.instruction ? (
              <InstructionPreviewBody
                summary={documentsData.instruction.summary}
                targetCategories={documentsData.instruction.target_categories ?? []}
                categoryLabels={documentsData.instruction.target_category_labels}
                tasks={
                  documentsData.instruction.tasks
                  ?? documentsData.instruction.task_descriptions?.map((t: { title: string; description: string }, i: number) => ({
                    no: i + 1,
                    title: t.title,
                    details: t.description ? [t.description] : [],
                  }))
                  ?? []
                }
              />
            ) : (
              <p style={{ fontSize: 12.5, color: COLORS.muted, padding: "16px 0" }}>
                課題文データがありません
              </p>
            )
          ) : documentsData?.questionnaireJson ? (
            <QuestionnaireDocument
              json={documentsData.questionnaireJson}
              patientName={documentsData?.patient?.name ?? undefined}
              meta={{
                age: documentsData?.patient?.age ?? undefined,
                gender: documentsData?.patient?.gender ?? undefined,
                birthDate: documentsData?.patient?.birthDate ?? undefined,
              }}
              compact={compact}
            />
          ) : (documentsData?.questionnaire || conversationData?.categoryTableText) ? (
            <pre
              style={{
                fontSize: 12,
                background: COLORS.bg,
                padding: 14,
                borderRadius: 8,
                border: `1px solid ${COLORS.line}`,
                overflow: "auto",
                maxHeight: "60vh",
                whiteSpace: "pre-wrap",
                fontFamily: FONT_FAMILY,
                lineHeight: 1.85,
              }}
            >
              {documentsData?.questionnaire || conversationData?.categoryTableText}
            </pre>
          ) : (
            <p style={{ fontSize: 12.5, color: COLORS.muted, padding: "16px 0" }}>
              問診票が未設定です。問診票を生成してください。
            </p>
          )}
        </div>
      </CollapsibleCard>
    );
  };

  const renderScoreHistoryCard = (height: number, compact = false) => {
    if (scoreHistoryPoints.length === 0 || scoreHistoryError) return null;
    const totalDotRadius = compact ? 2.8 : 3.2;
    const subDotRadius = compact ? 2.4 : 2.8;
    return (
      <Card>
        <CardLabel>スコア推移</CardLabel>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            margin: "2px 0 6px",
            fontSize: compact ? 11 : 11.5,
            fontWeight: 600,
            color: COLORS.muted,
          }}
        >
          <ChevronRight size={13} aria-hidden />
          {isMobile ? "グラフの点をタップすると、その回の評価を見られます" : "グラフの点をクリックすると、その回の評価を見られます"}
        </div>
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={scoreHistoryPoints}
              margin={{ top: compact ? 32 : 36, right: compact ? 8 : 16, left: 0, bottom: compact ? 4 : 5 }}
              onClick={handleScoreHistoryChartClick}
            >
              <CartesianGrid strokeDasharray="2 3" stroke={COLORS.line} />
              <XAxis
                dataKey="dateLabel"
                tick={{ fontSize: compact ? 10.5 : 11, fontFamily: FONT_FAMILY, fill: COLORS.muted }}
                stroke={COLORS.line}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: compact ? 10.5 : 11, fontFamily: FONT_FAMILY, fill: COLORS.muted }}
                stroke={COLORS.line}
                width={compact ? 28 : 32}
              />
              <Tooltip
                cursor={false}
                contentStyle={{
                  background: COLORS.surface,
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 8,
                  fontFamily: FONT_FAMILY,
                  fontSize: compact ? 11.5 : 12,
                }}
                labelFormatter={(_: unknown, payload?: Array<{ payload?: ScoreHistoryPoint }>) => {
                  const date = payload?.[0]?.payload?.date;
                  return date ? format(new Date(date), "yyyy/MM/dd") : "";
                }}
                formatter={(value: unknown) => [`${value}%`, ""]}
              />
              <Legend
                verticalAlign="top"
                align="right"
                wrapperStyle={{ fontFamily: FONT_FAMILY, fontSize: compact ? 11 : 12, top: 0, right: 0 }}
              />
              <Line
                type="monotone"
                dataKey="総合"
                stroke={COLORS.ink}
                strokeWidth={2}
                dot={(props: any) => renderScoreHistoryDot(props, COLORS.ink, totalDotRadius)}
                activeDot={(props: any) => renderScoreHistoryDot(props, COLORS.ink, totalDotRadius + 2)}
                name="総合達成率"
              />
              <Line
                type="monotone"
                dataKey="患者面接"
                stroke="#0E7490"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={(props: any) => renderScoreHistoryDot(props, "#0E7490", subDotRadius)}
                activeDot={(props: any) => renderScoreHistoryDot(props, "#0E7490", subDotRadius + 1.5)}
                name="患者面接"
                connectNulls
              />
              {usesDentistReportScore && (
                <Line
                  type="monotone"
                  dataKey="歯科医報告"
                  stroke="#7C3AED"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={(props: any) => renderScoreHistoryDot(props, "#7C3AED", subDotRadius)}
                  activeDot={(props: any) => renderScoreHistoryDot(props, "#7C3AED", subDotRadius + 1.5)}
                  name="歯科医報告"
                  connectNulls
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    );
  };

  // ===== Render =====

  if (isLoading || scorePending) {
    return (
      <div
        className="fixed inset-0 top-[68px] flex flex-col items-center justify-center gap-3"
        style={{ background: COLORS.bg, fontFamily: FONT_FAMILY }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.muted }} />
        {scorePending && (
          <div style={{ color: COLORS.muted, fontSize: 13, fontWeight: 700 }}>
            採点結果を生成中です。完了したら自動で表示します。
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="fixed inset-0 top-[68px] flex flex-col items-center justify-center gap-4"
        style={{ background: COLORS.bg, fontFamily: FONT_FAMILY, color: COLORS.ink }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, color: "#BE123C", letterSpacing: 1.5 }}>ERROR</div>
        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.3 }}>
          評価結果を読み込めませんでした
        </div>
        <p style={{ fontSize: 12.5, color: COLORS.muted, maxWidth: 520, textAlign: "center", lineHeight: 1.7 }}>
          {(error as Error)?.message}
          {!interviewId && <><br />{scoreListLabel.replace(/へ$/, '')}から該当の面接を選び直してください。</>}
        </p>
        <button
          type="button"
          onClick={handleBackToScores}
          style={{
            padding: "10px 18px",
            borderRadius: 8,
            background: COLORS.ink,
            color: COLORS.surface,
            border: "none",
            fontSize: 12.5,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          ← {scoreListLabel}
        </button>
        <ErrorModal isOpen={isErrorOpen} onClose={() => setIsErrorOpen(false)} message={errorMessage} />
      </div>
    );
  }

  if (!scoringResult && !dentistResult) {
    return null;
  }

  // patient strip データ（interviewMeta > documentsData.patient のフォールバック）
  const avatarName = interviewMeta?.avatarName ?? documentsData?.patient?.name ?? "—";
  const stripGender = interviewMeta?.gender ?? documentsData?.patient?.gender ?? "";
  const stripAge = interviewMeta?.ageRange
    ? ageRangeLabel(interviewMeta.ageRange)
    : documentsData?.patient?.age
    ? `${documentsData.patient.age}歳`
    : "";

  const patientGoodText = joinSummaryText([
    patientSections?.goodPoints,
    patientSummaryPendingText,
    !patientSections && scoringResult?.summaryComment ? scoringResult.summaryComment : undefined,
  ]);
  const patientImproveText = joinSummaryText([patientSections?.improvePoints]);
  const dentistGoodText = joinSummaryText([
    dentistSections?.goodPoints,
    dentistSummaryPendingText,
    !dentistSections && dentistResult?.summaryComment ? dentistResult.summaryComment : undefined,
  ]);
  const dentistImproveText = joinSummaryText([dentistSections?.improvePoints]);
  const showPatientSummary = Boolean(scoringResult || patientGoodText || patientImproveText);
  const showDentistSummary = Boolean(dentistResult || dentistGoodText || dentistImproveText);
  const hasEvaluationSummary = showPatientSummary || showDentistSummary;

  // 総評はふりかえりのタブの中に出す（患者面接の総評は患者面接タブ、報告の総評は報告タブ）。
  // ふりかえりを作れないときだけ、これまでどおり総評カードを出す
  const summaryInReview = Boolean(patientTaskReview || reportTaskReview);
  const taskReview = (
    <EvaluationTaskReview
      patient={patientTaskReview}
      report={reportTaskReview}
      patientPercent={scoringResult ? scorePercentage : null}
      reportPercent={usesDentistReportScore ? dentistScorePercentage : null}
      patientSummary={showPatientSummary ? (
        <EvaluationSummaryGroup
          label="患者面接結果"
          headerColor="#087A2A"
          goodText={patientGoodText}
          improveText={patientImproveText}
          goodTone="patientGood"
          improveTone="patientImprove"
        />
      ) : null}
      reportSummary={showDentistSummary ? (
        <EvaluationSummaryGroup
          label="歯科医師報告結果"
          headerColor="#1649B8"
          goodText={dentistGoodText}
          improveText={dentistImproveText}
          goodTone="dentistGood"
          improveTone="dentistImprove"
        />
      ) : null}
    />
  );

  if (isMobile) {

    return (
      <div
        className="fixed inset-0 flex flex-col overflow-hidden"
        style={{
          top: STUDENT_HEADER_HEIGHT,
          background: COLORS.bg,
          color: COLORS.ink,
          fontFamily: FONT_FAMILY,
          overscrollBehavior: "none",
        }}
      >
        <div
          style={{
            flex: "0 0 auto",
            padding: "18px 20px 16px",
            background: COLORS.surface,
            borderBottom: `1px solid ${COLORS.line}`,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: COLORS.muted,
              fontWeight: 800,
              letterSpacing: "0.15em",
              marginBottom: 8,
            }}
          >
            STEP 06・結果
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.4, lineHeight: 1.25 }}>
            面接評価結果
          </div>
        </div>

        <div
          style={{
            flex: "0 0 auto",
            padding: "14px 20px",
            background: COLORS.strip,
            borderBottom: `1px solid ${COLORS.line}`,
            display: "grid",
            gridTemplateColumns: "50px 1fr auto",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 999,
              background: COLORS.stripDot,
              display: "grid",
              placeItems: "center",
              fontSize: 16,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {avatarName.slice(0, 1)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25 }}>{avatarName}</div>
            {(stripAge || stripGender) && (
              <div style={{ marginTop: 4, fontSize: 12.5, color: COLORS.muted, fontWeight: 700 }}>
                {[stripAge, stripGender].filter(Boolean).join("・")}
              </div>
            )}
          </div>
          <span
            style={{
              padding: "7px 10px",
              borderRadius: 8,
              background: rankMeta.soft,
              color: rankMeta.color,
              border: `1px solid ${rankMeta.color}`,
              fontSize: 13,
              fontWeight: 800,
              whiteSpace: "nowrap",
            }}
          >
            Rank {totalRank}
          </span>
        </div>

        <div
          data-evaluation-scroll-container
          style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}
        >
          <div style={{ padding: "16px 20px 104px" }}>
            <Card>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 16, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12.5, color: COLORS.muted, fontWeight: 700, marginBottom: 4 }}>
                    総合スコア
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline" }}>
                    <span
                      style={{
                        fontSize: 78,
                        lineHeight: 1,
                        fontWeight: 800,
                        letterSpacing: -2,
                        color: COLORS.ink,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {Math.round(scoreAnimation)}
                    </span>
                    <span style={{ marginLeft: 4, fontSize: 20, fontWeight: 800, color: COLORS.muted }}>/ {learningScoreMax}点</span>
                  </div>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.75, color: COLORS.ink, fontWeight: 600 }}>
                  {usesDentistReportScore ? "患者面接と歯科衛生士への報告面接" : "患者面接"}の得点です。{rankMeta.sub}レンジ。
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
                {scoringResult && <ScoreBar label="患者面接" value={scorePercentage} color="#0E7490" unit="%" />}
                <ScoreBar
                  label="歯科衛生士への報告"
                  value={dentistScorePercentage}
                  color={usesDentistReportScore ? "#7C3AED" : "#A1A1AA"}
                  valueLabel={usesDentistReportScore ? undefined : "実施なし"}
                  unit={usesDentistReportScore ? "%" : ""}
                />
              </div>
            </Card>

            <OnePointLessonCard lesson={onePointLesson} />

            {taskReview}

            {!summaryInReview && hasEvaluationSummary && (
              <Card>
                <CardLabel>総評</CardLabel>
                <EvaluationSummaryPanels
                  stacked
                  showPatient={showPatientSummary}
                  showDentist={showDentistSummary}
                  patientGoodText={patientGoodText}
                  patientImproveText={patientImproveText}
                  dentistGoodText={dentistGoodText}
                  dentistImproveText={dentistImproveText}
                />
              </Card>
            )}

            <CollapsibleCard label="会話ログ">
              <div style={{ paddingTop: 12 }}>
                {(() => {
                  const entries = conversationData?.conversations ?? [];
                  const patientEntries: ConversationEntry[] = [];
                  const dentistEntries: ConversationEntry[] = [];
                  entries.forEach((e: EvaluationConversationApiEntry, i: number) => {
                    const convEntry: ConversationEntry = {
                      id: String(i),
                      type: e.role === 'user' ? 'question' : 'response',
                      text: e.content,
                      timestamp: new Date(e.timestamp ?? Date.now()),
                      roundId: e.roundId,
                      hasUserAudio: e.hasUserAudio,
                    };
                    if (e.speakerType === 'dentist') dentistEntries.push(convEntry);
                    else patientEntries.push(convEntry);
                  });

                  if (entries.length === 0) {
                    return <p style={{ fontSize: 12.5, color: COLORS.muted, padding: "16px 0" }}>会話履歴がありません</p>;
                  }

                  const renderHighlighted = (entry: ConversationEntry) => (
                    <HighlightedConversation content={entry.text} evidenceStrings={evidenceStrings} type="listening" />
                  );

                  return (
                    <>
                      {patientEntries.length > 0 && (
                        <>
                          <SectionDivider label="患者面接" />
                          <ConversationLog
                            conversations={patientEntries}
                            counterpartLabel="患者"
                            counterpartBubbleBg="#F4F4F5"
                            counterpartIcon={patientLogIcon}
                            studentIcon={learnerLogIcon}
                            renderText={renderHighlighted}
                            renderActions={renderStaffRecordingAction}
                            height="auto"
                          />
                        </>
                      )}
                      {dentistEntries.length > 0 && (
                        <>
                          <SectionDivider label="歯科医への報告" />
                          <ConversationLog
                            conversations={dentistEntries}
                            counterpartLabel="歯科医"
                            counterpartBubbleBg="#F5F3FF"
                            counterpartIcon={dentistLogIcon}
                            studentIcon={learnerLogIcon}
                            renderText={renderHighlighted}
                            renderActions={renderStaffRecordingAction}
                            height="auto"
                          />
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </CollapsibleCard>

            {renderEvaluationDocumentsCard(true)}

            {renderScoreHistoryCard(220, true)}

            {showExperimentSurvey && (
              <div style={{ marginTop: 16 }}>
                <ExperimentPostSurveyForm
                  interviewId={parsedInterviewId}
                  participantCode={currentUser?.participantCode}
                  embedded
                  onSubmitted={() => setIsSurveyCompletionOpen(true)}
                />
              </div>
            )}

          </div>
        </div>

        <div
          style={{
            position: "absolute",
            left: 20,
            right: 20,
            bottom: "max(18px, env(safe-area-inset-bottom))",
            display: showExperimentSurvey ? "none" : "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          {!isPublicSample && !isDirectEntryUser && interviewMeta?.conditionInstructionId && (
            <button
              type="button"
              onClick={() =>
                retryWithInstructionMutation.mutate({
                  instructionId: interviewMeta.conditionInstructionId!,
                  avatarId: interviewMeta.avatarId,
                })
              }
              disabled={retryWithInstructionMutation.isPending}
              style={{
                minHeight: 58,
                padding: "0 14px",
                borderRadius: 999,
                background: COLORS.surface,
                color: COLORS.ink,
                border: `1px solid ${COLORS.line}`,
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                cursor: retryWithInstructionMutation.isPending ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                fontSize: 12.5,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                flex: "0 1 auto",
                minWidth: 0,
              }}
            >
              {retryWithInstructionMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span aria-hidden>↻</span>
              )}
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {retryButtonLabel}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={handleBackToScores}
            style={{
              minHeight: 58,
              padding: "0 14px",
              borderRadius: 999,
              background: COLORS.surface,
              color: COLORS.ink,
              border: `1px solid ${COLORS.line}`,
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12.5,
              fontWeight: 800,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
              flex: "0 0 auto",
              minWidth: 104,
              whiteSpace: "nowrap",
            }}
          >
            スコア一覧
          </button>
          {!isPublicSample && <button
            type="button"
            onClick={handleBackToSelect}
            style={{
              minHeight: 64,
              padding: "0 18px",
              borderRadius: 999,
              background: "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
              color: "#FFFFFF",
              border: "none",
              boxShadow: "0 0 0 4px rgba(255,138,61,0.15), 0 10px 28px rgba(255,107,26,0.42), 0 2px 6px rgba(0,0,0,0.12)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 800,
              letterSpacing: 0.3,
              flex: 1,
              minWidth: 0,
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {directEntryMutation.isPending ? "面接を準備中..." : backToSelectLabel}
            </span>
          </button>}
        </div>

        <ExperimentSurveyCompletionDialog
          open={isSurveyCompletionOpen}
          isStarting={directEntryMutation.isPending}
          onOpenChange={setIsSurveyCompletionOpen}
          onReturnToEvaluation={handleReturnToEvaluation}
          onStartInterview={handleStartInterviewAfterSurvey}
        />

        <ErrorModal isOpen={isErrorOpen} onClose={() => setIsErrorOpen(false)} message={errorMessage} />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 top-[68px] flex flex-col overflow-hidden"
      style={{
        background: COLORS.bg,
        color: COLORS.ink,
        fontFamily: FONT_FAMILY,
      }}
    >
      {/* Header bar */}
      <div
        style={{
          padding: "20px 32px 24px",
          background: COLORS.surface,
          borderBottom: `1px solid ${COLORS.line}`,
          display: "flex",
          alignItems: "center",
          gap: 16,
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={handleBackToScores}
          style={{
            padding: "6px 12px",
            border: `1px solid ${COLORS.line}`,
            background: COLORS.surface,
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            color: COLORS.ink,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ← {scoreListLabel.replace(/へ$/, '')}
        </button>
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: COLORS.muted,
              fontWeight: 700,
              letterSpacing: 1.5,
              marginBottom: 6,
            }}
          >
            STEP 06 · 結果
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.2 }}>
            面接評価結果
          </div>
        </div>
        <div
          style={{
            marginLeft: "auto",
            fontSize: 14,
            fontWeight: 600,
            color: COLORS.muted,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {format(new Date(), "yyyy/MM/dd")}
        </div>
      </div>

      {/* Patient summary strip — cream (scenario-preview と同じ雰囲気) */}
      <div
        style={{
          padding: "14px 32px",
          background: COLORS.strip,
          borderBottom: `1px solid ${COLORS.line}`,
          display: "flex",
          alignItems: "center",
          gap: 20,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            background: COLORS.stripDot,
            display: "grid",
            placeItems: "center",
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          👤
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{avatarName}</div>
            {(stripAge || stripGender) && (
              <div style={{ fontSize: 14, color: COLORS.muted, fontWeight: 600 }}>
                {[stripAge, stripGender].filter(Boolean).join("・")}
              </div>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#4B4738", lineHeight: 1.6 }}>
            面接評価結果です。総合・ふりかえり・会話ログをご確認ください。
          </div>
        </div>
        <RankChip rank={totalRank} meta={rankMeta} />
      </div>

      {/* Scrollable content — 3 columns (数値系 / 総評 / 詳細) */}
      <div data-evaluation-scroll-container style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ maxWidth: 1600, margin: "0 auto", padding: "20px 32px 140px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 10fr) minmax(0, 8fr) minmax(0, 8fr)",
              gap: 16,
              alignItems: "start",
            }}
          >

          {/* ========== Left column (数値系) ========== */}
          <div>

          {/* ---- Overall score card ---- */}
          <Card>
            {/* 上段: 総合スコア（左） / RankChip（右上端） */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.3, color: COLORS.ink }}>
                総合スコア
              </div>
              <span
                style={{
                  padding: "6px 14px",
                  borderRadius: 999,
                  background: rankMeta.soft,
                  color: rankMeta.color,
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  flexShrink: 0,
                }}
              >
                Rank {totalRank}
              </span>
            </div>
            {/* 2カラム: 左=大きい数字(45%) / 右=3要素縦積み(55%) */}
            <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
              <div
                style={{
                  flex: "0 0 45%",
                  minWidth: 180,
                  textAlign: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 108,
                    fontWeight: 800,
                    lineHeight: 1,
                    letterSpacing: -3,
                    fontVariantNumeric: "tabular-nums",
                    color: rankMeta.color,
                  }}
                >
                  {Math.round(scoreAnimation)}
                </span>
                <span style={{ fontSize: 28, fontWeight: 700, marginLeft: 6, color: COLORS.muted, verticalAlign: "baseline" }}>/ {learningScoreMax}点</span>
              </div>
              <div style={{ flex: "0 0 calc(55% - 24px)", minWidth: 240, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 13, lineHeight: 1.7, color: COLORS.ink, fontWeight: 600 }}>
                  {usesDentistReportScore ? "患者面接と歯科医報告" : "患者面接"}の得点です。{rankMeta.sub}レンジ。
                </div>
                <div style={{ height: 1, background: COLORS.line }} />
                {scoringResult && (
                  <ScoreBar
                    label="患者面接"
                    value={scorePercentage}
                    color="#1E40AF"
                    unit="%"
                  />
                )}
                <ScoreBar
                  label="歯科医報告"
                  value={dentistScorePercentage}
                  color={usesDentistReportScore ? "#6D28D9" : "#A1A1AA"}
                  valueLabel={usesDentistReportScore ? undefined : "実施なし"}
                  unit={usesDentistReportScore ? "%" : ""}
                />
              </div>
            </div>
          </Card>

          </div>

          {/* ========== Middle column (総評) ========== */}
          <div>

            <OnePointLessonCard lesson={onePointLesson} />

          {taskReview}

          {/* ---- Summary (2x2 matrix)。ふりかえりを作れないときだけ ---- */}
          {!summaryInReview && hasEvaluationSummary && (
            <Card>
              <CardLabel>総評</CardLabel>
              <EvaluationSummaryPanels
                showPatient={showPatientSummary}
                showDentist={showDentistSummary}
                patientGoodText={patientGoodText}
                patientImproveText={patientImproveText}
                dentistGoodText={dentistGoodText}
                dentistImproveText={dentistImproveText}
              />
            </Card>
          )}

          </div>

          {/* ========== Right column (詳細折りたたみ) ========== */}
          <div>

          <CollapsibleCard label="会話ログ">
            <div style={{ paddingTop: 12 }}>
              {(() => {
                const entries = conversationData?.conversations ?? [];
                const patientEntries: ConversationEntry[] = [];
                const dentistEntries: ConversationEntry[] = [];
                entries.forEach((e: EvaluationConversationApiEntry, i: number) => {
                  const convEntry: ConversationEntry = {
                    id: String(i),
                    type: e.role === 'user' ? 'question' : 'response',
                    text: e.content,
                    timestamp: new Date(e.timestamp ?? Date.now()),
                    roundId: e.roundId,
                    hasUserAudio: e.hasUserAudio,
                  };
                  if (e.speakerType === 'dentist') dentistEntries.push(convEntry);
                  else patientEntries.push(convEntry);
                });

                if (entries.length === 0) {
                  return (
                    <p style={{ fontSize: 12.5, color: COLORS.muted, padding: "16px 0" }}>
                      会話履歴がありません
                    </p>
                  );
                }

                const renderHighlighted = (entry: ConversationEntry) => (
                  <HighlightedConversation
                    content={entry.text}
                    evidenceStrings={evidenceStrings}
                    type="listening"
                  />
                );

                return (
                  <>
                    {patientEntries.length > 0 && (
                      <>
                        <SectionDivider label="患者面接" />
                        <ConversationLog
                          conversations={patientEntries}
                          counterpartLabel="患者"
                          counterpartBubbleBg="#F3F4F6"
                          counterpartIcon={patientLogIcon}
                          studentIcon={learnerLogIcon}
                          renderText={renderHighlighted}
                          renderActions={renderStaffRecordingAction}
                          height="auto"
                        />
                      </>
                    )}
                    {dentistEntries.length > 0 && (
                      <>
                        <SectionDivider label="歯科医への報告" />
                        <ConversationLog
                          conversations={dentistEntries}
                          counterpartLabel="歯科医"
                          counterpartBubbleBg="#F5F3FF"
                          counterpartIcon={dentistLogIcon}
                          studentIcon={learnerLogIcon}
                          renderText={renderHighlighted}
                          renderActions={renderStaffRecordingAction}
                          height="auto"
                        />
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </CollapsibleCard>

          {renderEvaluationDocumentsCard(false)}

          {renderScoreHistoryCard(240, false)}

          </div>

          </div>

          {showExperimentSurvey && (
            <div style={{ marginTop: 20 }}>
              <ExperimentPostSurveyForm
                interviewId={parsedInterviewId}
                participantCode={currentUser?.participantCode}
                embedded
                onSubmitted={() => setIsSurveyCompletionOpen(true)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bottom CTA bar: 実証実験では再挑戦を出さず、割り当て面接へ戻す */}
      <div
        style={{
          position: "absolute",
          right: 24,
          bottom: 24,
          display: showExperimentSurvey ? "none" : "flex",
          gap: 12,
          alignItems: "center",
        }}
      >
        {!isPublicSample && !isDirectEntryUser && interviewMeta?.conditionInstructionId && (
          <button
            type="button"
            onClick={() =>
              retryWithInstructionMutation.mutate({
                instructionId: interviewMeta.conditionInstructionId!,
                avatarId: interviewMeta.avatarId,
              })
            }
            disabled={retryWithInstructionMutation.isPending}
            style={{
              padding: "12px 18px",
              borderRadius: 999,
              background: COLORS.surface,
              color: COLORS.ink,
              border: `1px solid ${COLORS.line}`,
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              cursor: retryWithInstructionMutation.isPending ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {retryWithInstructionMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <span aria-hidden>↻</span>
            )}
            {retryButtonLabel}
          </button>
        )}
        <button
          type="button"
          onClick={handleBackToScores}
          style={{
            padding: "14px 22px",
            borderRadius: 999,
            background: COLORS.surface,
            color: COLORS.ink,
            border: `1px solid ${COLORS.line}`,
            boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontFamily: "inherit",
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 0.3,
          }}
        >
          スコア一覧
        </button>
        {!isPublicSample && <button
          type="button"
          onClick={handleBackToSelect}
          style={{
            padding: "14px 22px",
            borderRadius: 999,
            background: "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
            color: "#fff",
            border: "none",
            boxShadow:
              "0 0 0 4px rgba(255,138,61,0.15), 0 10px 28px rgba(255,107,26,0.45), 0 2px 6px rgba(0,0,0,0.12)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontFamily: "inherit",
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 0.3,
            transition: "transform 150ms ease-out",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          {directEntryMutation.isPending ? "面接を準備中..." : backToSelectLabel}
        </button>}
      </div>

      <ExperimentSurveyCompletionDialog
        open={isSurveyCompletionOpen}
        isStarting={directEntryMutation.isPending}
        onOpenChange={setIsSurveyCompletionOpen}
        onReturnToEvaluation={handleReturnToEvaluation}
        onStartInterview={handleStartInterviewAfterSurvey}
      />

      <ErrorModal isOpen={isErrorOpen} onClose={() => setIsErrorOpen(false)} message={errorMessage} />
    </div>
  );
}

// ===== Shared building blocks =====

function ExperimentSurveyCompletionDialog({
  open,
  isStarting,
  onOpenChange,
  onReturnToEvaluation,
  onStartInterview,
}: {
  open: boolean;
  isStarting: boolean;
  onOpenChange: (open: boolean) => void;
  onReturnToEvaluation: () => void;
  onStartInterview: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-sm rounded-lg border-zinc-200 p-5 sm:p-6">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl leading-7 text-zinc-950">
            アンケートを送信しますか
          </DialogTitle>
          <DialogDescription className="pt-2 text-base leading-6 text-zinc-700">
            次の面接を開始
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <Button
            type="button"
            onClick={onStartInterview}
            disabled={isStarting}
            className={`min-h-11 font-bold ${EXPERIMENT_PRIMARY_ACTION_CLASS}`}
          >
            {isStarting ? "準備中..." : "はい"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onReturnToEvaluation}
            className="min-h-11 border-zinc-300 font-bold"
          >
            いいえ
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function joinSummaryText(parts: Array<string | undefined | null>): string {
  return parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join("\n\n");
}

type EvaluationSummaryTone = "patientGood" | "patientImprove" | "dentistGood" | "dentistImprove";

const EVALUATION_SUMMARY_PALETTE: Record<
  EvaluationSummaryTone,
  { title: string; background: string; border: string; body: string }
> = {
  patientGood: { title: "#087A2A", background: "#DDF7E5", border: "#9BE3B0", body: "#174D28" },
  patientImprove: { title: "#A33A00", background: "#FFF2BE", border: "#F3CE55", body: "#613A00" },
  dentistGood: { title: "#075BC7", background: "#DCEBFC", border: "#9EC7F5", body: "#123F76" },
  dentistImprove: { title: "#C51414", background: "#FFD5D0", border: "#F4897F", body: "#741B1B" },
};

function EvaluationSummaryPanels({
  stacked = false,
  showPatient,
  showDentist,
  patientGoodText,
  patientImproveText,
  dentistGoodText,
  dentistImproveText,
}: {
  stacked?: boolean;
  showPatient: boolean;
  showDentist: boolean;
  patientGoodText: string;
  patientImproveText: string;
  dentistGoodText: string;
  dentistImproveText: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: stacked ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
        gap: 14,
        alignItems: "start",
      }}
    >
      {showPatient && (
        <EvaluationSummaryGroup
          label="患者面接結果"
          headerColor="#087A2A"
          goodText={patientGoodText}
          improveText={patientImproveText}
          goodTone="patientGood"
          improveTone="patientImprove"
        />
      )}
      {showDentist && (
        <EvaluationSummaryGroup
          label="歯科医師報告結果"
          headerColor="#1649B8"
          goodText={dentistGoodText}
          improveText={dentistImproveText}
          goodTone="dentistGood"
          improveTone="dentistImprove"
        />
      )}
    </div>
  );
}

function EvaluationSummaryGroup({
  label,
  headerColor,
  goodText,
  improveText,
  goodTone,
  improveTone,
}: {
  label: string;
  headerColor: string;
  goodText: string;
  improveText: string;
  goodTone: EvaluationSummaryTone;
  improveTone: EvaluationSummaryTone;
}) {
  return (
    <section
      style={{
        minWidth: 0,
        padding: 10,
        border: `1px solid ${COLORS.line}`,
        borderRadius: 12,
        background: COLORS.surface,
      }}
    >
      <div
        style={{
          marginBottom: 10,
          padding: "9px 12px",
          borderRadius: 6,
          background: headerColor,
          color: "#FFFFFF",
          fontSize: 17,
          fontWeight: 800,
          letterSpacing: 1.2,
          textAlign: "center",
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <EvaluationSummaryBlock title="良かった点" text={goodText} tone={goodTone} icon="✓" />
        <EvaluationSummaryBlock title="改善できる点" text={improveText} tone={improveTone} icon="!" />
      </div>
    </section>
  );
}

function EvaluationSummaryBlock({
  title,
  text,
  tone,
  icon,
}: {
  title: string;
  text: string;
  tone: EvaluationSummaryTone;
  icon: string;
}) {
  const palette = EVALUATION_SUMMARY_PALETTE[tone];
  return (
    <div
      style={{
        minWidth: 0,
        minHeight: 112,
        padding: "13px 14px",
        borderRadius: 9,
        background: palette.background,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          marginBottom: 8,
          color: palette.title,
          fontSize: 13,
          fontWeight: 800,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-grid",
            width: 18,
            height: 18,
            placeItems: "center",
            borderRadius: 999,
            background: palette.title,
            color: "#FFFFFF",
            fontSize: 12,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
        {title}
      </div>
      <p
        style={{
          margin: 0,
          color: text ? palette.body : COLORS.muted,
          fontSize: 12.5,
          lineHeight: 1.8,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {text || "総評は記録されていません。"}
      </p>
    </div>
  );
}

function ageRangeLabel(range: string): string {
  if (range === "20-34") return "20〜34歳";
  if (range === "35-49") return "35〜49歳";
  if (range === "50-64") return "50〜64歳";
  if (range === "65+") return "65歳以上";
  if (range === "50+") return "50歳以上";
  return range;
}

function RankChip({ rank, meta }: { rank: Rank; meta: (typeof RANK_META)[Rank] }) {
  return (
    <span
      style={{
        padding: "6px 14px",
        borderRadius: 999,
        background: meta.soft,
        color: meta.color,
        fontSize: 12,
        fontWeight: 800,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
        letterSpacing: 0.3,
      }}
    >
      <span style={{ fontFamily: "system-ui", fontSize: 12, letterSpacing: 1.5 }}>{meta.dots}</span>
      Rank {rank} · {meta.sub}
    </span>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: COLORS.surface,
        borderRadius: 12,
        padding: "12px 20px",
        border: `1px solid ${COLORS.line}`,
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        margin: "12px 0 4px",
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: 1.5,
        color: COLORS.muted,
        textTransform: "uppercase",
      }}
    >
      <span style={{ flex: 1, height: 1, background: COLORS.line }} />
      <span>{label}</span>
      <span style={{ flex: 1, height: 1, background: COLORS.line }} />
    </div>
  );
}

function CardLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 18,
        fontWeight: 800,
        letterSpacing: -0.3,
        color: COLORS.ink,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function OnePointLessonCard({ lesson }: { lesson?: ScoreApiResponse["onePointLesson"] }) {
  if (!lesson?.message) return null;

  return (
    <Card>
      <CardLabel>{lesson.title || "ワンポイントレッスン"}</CardLabel>
      <p style={{ fontSize: 13, lineHeight: 1.8, color: COLORS.ink, margin: 0, whiteSpace: "pre-wrap" }}>
        {lesson.message}
      </p>
    </Card>
  );
}

function CollapsibleCard({
  label,
  children,
  metricChip,
  defaultOpen,
}: {
  label: string;
  children: React.ReactNode;
  metricChip?: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <div
      style={{
        background: COLORS.surface,
        borderRadius: 12,
        border: `1px solid ${COLORS.line}`,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <Collapsible defaultOpen={defaultOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            style={{
              width: "100%",
              padding: "16px 20px",
              background: "transparent",
              border: "none",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              cursor: "pointer",
              fontFamily: "inherit",
              color: COLORS.ink,
            }}
            className="group"
          >
            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.3 }}>{label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
              {metricChip}
              <ChevronDown
                className="w-4 h-4 transition-transform group-data-[state=open]:rotate-180"
                style={{ color: COLORS.muted }}
              />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div style={{ padding: "0 20px 20px" }}>{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}


function ScoreBar({
  label,
  value,
  color,
  unit = "点",
  valueLabel,
}: {
  label: string;
  value: number;
  color: string;
  unit?: string;
  valueLabel?: string;
}) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: COLORS.ink }}>{label}</span>
        <span
          style={{
            fontSize: 15,
            fontWeight: 800,
            fontVariantNumeric: "tabular-nums",
            color: COLORS.ink,
          }}
        >
          {valueLabel ?? value}
          {valueLabel ? null : <span style={{ fontSize: 11, fontWeight: 700, marginLeft: 2, color: COLORS.muted }}>{unit}</span>}
        </span>
      </div>
      <div
        style={{
          height: 8,
          background: COLORS.line,
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: "100%",
            background: color,
            borderRadius: 999,
            transition: "width 900ms cubic-bezier(0.19, 1, 0.22, 1)",
          }}
        />
      </div>
    </div>
  );
}

function DocTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "12px 18px",
        border: "none",
        background: "transparent",
        borderBottom: active ? `3px solid ${COLORS.ink}` : "3px solid transparent",
        color: active ? COLORS.ink : COLORS.muted,
        fontSize: 13,
        fontWeight: active ? 800 : 600,
        cursor: "pointer",
        fontFamily: "inherit",
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}
